import {
  ActivityStats,
  Challenge,
  ChallengeCard,
  DistanceIcon,
  Event,
  Geogroup,
  GeogroupNewsPost,
  HighlightedEventCard,
  LinkIcon,
  NewsPostCard,
  PlaceIcon,
  Ride,
  SavedCO2Icon,
  UserChallenge,
  UserGeogroup,
  defaultEnvironment,
  useUnits,
} from '@geovelo-frontends/commons';
import { Add, ChevronRight } from '@mui/icons-material';
import { Box, ButtonBase, Divider, Skeleton, Typography } from '@mui/material';
import { Link, navigate } from 'gatsby';
import React, { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';
import { DialogWithContent } from '../../components/dialog';
import RideCard from '../../components/ride-card';
import StatCard from '../../components/stat-card';
import { AppContext } from '../../context';

function Profile({
  geogroup,
  userGeogroup,
  activities,
  challenges,
  userChallenges,
  joiningChallenge,
  highlightedEvent,
  highlightedEventChallenge,
  newsPosts,
  rides,
  handleNewsPostFormDialogOpen,
  handleNewsPostRemoved,
  handleChallengeJoin,
  setHighlightedEventChallenge,
  setTab,
}: {
  activities?: ActivityStats[];
  challenges?: Challenge[];
  geogroup: Geogroup | undefined;
  handleChallengeJoin: (challenge: Challenge) => void;
  handleNewsPostFormDialogOpen: (post?: GeogroupNewsPost) => void;
  handleNewsPostRemoved: () => void;
  highlightedEvent: Event | null | undefined;
  highlightedEventChallenge: Challenge | null | undefined;
  joiningChallenge: { [key: number]: boolean };
  newsPosts?: GeogroupNewsPost[];
  rides?: Ride[];
  setHighlightedEventChallenge: (challenge: Challenge | null) => void;
  setTab: (tab: 'news' | 'rides' | 'challenges') => void;
  userChallenges?: UserChallenge[];
  userGeogroup?: UserGeogroup | null;
}): React.JSX.Element {
  const {
    user: { current: currentUser },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { toDistance } = useUnits();

  return (
    <>
      {geogroup &&
        userGeogroup &&
        geogroup.type !== 'other' &&
        geogroup.type !== 'company' &&
        !geogroup.partner &&
        userGeogroup.isAdmin && (
          <HighlightedEventCard
            Button={Button}
            displayButtonProps={
              highlightedEventChallenge
                ? ({
                    component: Link,
                    to: `/${currentLanguage}/communities/${geogroup.id}/challenges/${highlightedEventChallenge.id}`,
                    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                  } as any)
                : {}
            }
            geogroup={geogroup}
            highlightedEvent={highlightedEvent}
            highlightedEventChallenge={highlightedEventChallenge}
            onJoined={(challenge) => {
              setHighlightedEventChallenge(challenge);
              navigate(`/${currentLanguage}/communities/${geogroup.id}/challenges/${challenge.id}`);
            }}
          />
        )}
      {(!geogroup || geogroup.description || geogroup.link) && (
        <Box>
          {geogroup ? (
            <>
              {geogroup.description && <Typography>{geogroup.description}</Typography>}
              {geogroup.link && (
                <Box>
                  <Button
                    color="primary"
                    href={geogroup.link.url}
                    rel="noreferrer"
                    size="small"
                    startIcon={<LinkIcon />}
                    target="_blank"
                    variant="text"
                  >
                    {geogroup.link.label || geogroup.link.url}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="50%" />
              </Typography>
            </>
          )}
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap={4}>
        <Typography component="h2" fontWeight={700} variant="h6">
          {t('geovelo.communities.profile.stats.title')}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <Box width={{ xs: '100%', sm: 'calc((100% - 32px) / 3)' }}>
            <StatCard
              bgColor="#EAE2FC"
              color="#905EFA"
              Icon={PlaceIcon}
              label={t('commons.stats_types.journeys')}
              progression={
                activities && activities.length > 0
                  ? `${activities[0].nbRoutes > activities[1].nbRoutes ? '+ ' : '- '}${t(
                      'geovelo.communities.activities.rides',
                      { count: Math.abs(activities[0].nbRoutes - activities[1].nbRoutes) },
                    )} vs ${t('commons.periods.last_month').toLowerCase()}`
                  : undefined
              }
              value={
                activities ? (activities.length === 0 ? '-' : activities[0].nbRoutes) : undefined
              }
            />
          </Box>
          <Box width={{ xs: '100%', sm: 'calc((100% - 32px) / 3)' }}>
            <StatCard
              bgColor="#FFF5D0"
              color="#BB8A1A"
              Icon={DistanceIcon}
              label={t('geovelo.communities.profile.stats.distance_label')}
              progression={
                activities && activities.length > 0
                  ? `${activities[0].distance > activities[1].distance ? '+ ' : '- '}${toDistance(
                      Math.abs(activities[0].distance - activities[1].distance),
                    )} vs ${t('commons.periods.last_month').toLowerCase()}`
                  : undefined
              }
              value={
                activities
                  ? activities.length === 0
                    ? '-'
                    : toDistance(activities[0].distance)
                  : undefined
              }
            />
          </Box>
          <Box width={{ xs: '100%', sm: 'calc((100% - 32px) / 3)' }}>
            <StatCard
              bgColor="#DAF3DF"
              color="#03966A"
              Icon={SavedCO2Icon}
              label={t('commons.routing.detail.co2_saved')}
              progression={
                activities && activities.length > 0
                  ? `${activities[0].distance > activities[1].distance ? '+ ' : '- '}${(
                      (Math.abs(activities[0].distance - activities[1].distance) / 1000) *
                      defaultEnvironment.savedKgOfCO2PerKilometer
                    ).toFixed(1)} kg vs ${t('commons.periods.last_month').toLowerCase()}`
                  : undefined
              }
              value={
                activities
                  ? activities.length === 0
                    ? '-'
                    : `${(
                        (activities[0].distance / 1000) *
                        defaultEnvironment.savedKgOfCO2PerKilometer
                      ).toFixed(1)} kg`
                  : undefined
              }
            />
          </Box>
        </Box>
      </Box>
      {(!newsPosts ||
        (userGeogroup?.isAdmin && (!!geogroup?.partner || geogroup?.type !== 'city')) ||
        newsPosts.length > 0) && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Box alignItems="center" display="flex" gap={2} justifyContent="space-between">
            <Typography component="h2" fontWeight={700} variant="h6">
              {t('geovelo.communities.news.title')}
            </Typography>
            {newsPosts && newsPosts.length > 0 && (
              <Button
                color="primary"
                endIcon={<ChevronRight />}
                onClick={() => setTab('news')}
                size="small"
                variant="text"
              >
                {t('commons.news.actions.view_all')}
              </Button>
            )}
          </Box>
          <Box display="flex" flexDirection="column" gap={3}>
            {newsPosts
              ? newsPosts.map((newsPost, index) => (
                  <Fragment key={newsPost.id}>
                    {index > 0 && <Divider />}
                    <NewsPostCard
                      currentPartner={geogroup?.partner}
                      currentUser={currentUser}
                      Dialog={DialogWithContent}
                      geogroup={geogroup}
                      newsPost={newsPost}
                      onEdit={() => handleNewsPostFormDialogOpen(newsPost)}
                      onRemoved={handleNewsPostRemoved}
                      userType={userGeogroup?.isAdmin ? 'admin' : 'user'}
                    />
                  </Fragment>
                ))
              : [0, 1, 2].map((key, index) => (
                  <Fragment key={key}>
                    {index > 0 && <Divider />}
                    <NewsPostCard />
                  </Fragment>
                ))}
          </Box>
          {geogroup && geogroup.type !== 'city' && (
            <Box
              alignItems={newsPosts && newsPosts.length === 0 ? 'center' : 'flex-end'}
              display="flex"
              flexDirection="column"
              gap={2}
            >
              {newsPosts && newsPosts.length === 0 && (
                <Typography>{t('geovelo.communities.news.empty_state')}</Typography>
              )}
              <Button
                color="primary"
                disabled={!newsPosts}
                onClick={() => handleNewsPostFormDialogOpen()}
                size="small"
                startIcon={<Add />}
                variant={newsPosts && newsPosts.length === 0 ? 'contained' : 'outlined'}
              >
                {t('geovelo.communities.actions.create_post')}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {(!challenges || challenges.length > 0) && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Box alignItems="center" display="flex" gap={2} justifyContent="space-between">
            <Typography component="h2" fontWeight={700} variant="h6">
              {t('geovelo.communities.challenges.title')}
            </Typography>
            <Button
              color="primary"
              endIcon={<ChevronRight />}
              onClick={() => setTab('challenges')}
              size="small"
              variant="text"
            >
              {t('commons.challenges.actions.view_all')}
            </Button>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={3}>
            {geogroup && challenges && userChallenges
              ? challenges.map((challenge) => {
                  const userChallenge =
                    userChallenges.find(
                      ({ challenge: { id: challengeId } }) => challengeId === challenge.id,
                    ) || null;
                  const joinable = challenge.period !== 'past' && !userChallenge && currentUser;

                  return (
                    <Box
                      flexShrink={0}
                      key={challenge.id}
                      width={{
                        xs: '100%',
                        md: 'calc((100% - 24px) / 2)',
                        lg: 'calc((100% - 48px) / 3)',
                      }}
                    >
                      <ChallengeCard
                        enableUserProgression
                        challenge={challenge}
                        disableLink={joiningChallenge[challenge.id] || !userChallenge}
                        handleClick={joinable ? () => handleChallengeJoin(challenge) : undefined}
                        Link={Link}
                        redirectLink={`/${currentLanguage}/communities/${geogroup.id}/challenges/${challenge.id}`}
                        userChallenge={userChallenge}
                      />
                    </Box>
                  );
                })
              : [0, 1, 2].map((key) => (
                  <Box
                    flexShrink={0}
                    key={key}
                    width={{
                      xs: '100%',
                      md: 'calc((100% - 24px) / 2)',
                      lg: 'calc((100% - 48px) / 3)',
                    }}
                  >
                    <ChallengeCard disableLink enableUserProgression />
                  </Box>
                ))}
          </Box>
        </Box>
      )}
      {geogroup?.partner && (!rides || rides.length > 0) && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Box alignItems="center" display="flex" gap={2} justifyContent="space-between">
            <Typography component="h2" fontWeight={700} variant="h6">
              {t('geovelo.navigation.rides')}
            </Typography>
            <Button
              color="primary"
              endIcon={<ChevronRight />}
              onClick={() => setTab('rides')}
              size="small"
              variant="text"
            >
              {t('geovelo.rides.actions.view_all')}
            </Button>
          </Box>
          <Box display="flex" flexDirection="column" gap={3}>
            {rides
              ? rides.map((ride) => (
                  <ButtonBase
                    component={Link}
                    key={ride.id}
                    sx={{ borderRadius: 4 }}
                    to={`/${currentLanguage}/rides/${ride.id}`}
                  >
                    <RideCard outlined row ride={ride} />
                  </ButtonBase>
                ))
              : [0, 1, 2].map((key) => <RideCard outlined row key={key} />)}
          </Box>
        </Box>
      )}
    </>
  );
}

export default Profile;
